import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import MainURL from '../../MainURL';
import { CONSOLE_INFO, isConsoleKey, saveUserSession, type ConsoleKey } from './userSession';
import './Login.scss';

type LoginResponse = {
  ok: boolean;
  message?: string;
  user?: unknown;
};

// 로그인할 콘솔(?console=app|roll|finance). 콘솔마다 로그인이 따로라, 로그인하면 그 콘솔 세션만 생긴다.
function resolveConsole(value: string | null): ConsoleKey {
  return isConsoleKey(value) ? value : 'app';
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const consoleKey = resolveConsole(searchParams.get('console'));
  const consoleInfo = CONSOLE_INFO[consoleKey];
  const [userAccount, setUserAccount] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const login = async () => {
    const trimmedAccount = userAccount.trim();
    if (!trimmedAccount || !password) {
      alert('아이디와 비밀번호를 입력해 주세요.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post<LoginResponse>(`${MainURL}/login`, {
        userAccount: trimmedAccount,
        password,
      });

      if (res.data?.ok && res.data.user) {
        // 재정앱은 선택 서비스 - 이 교회가 쓰지 않으면 재정관리 콘솔에 들어가지 않는다.
        if (consoleKey === 'finance' && (res.data.user as { financeEnabled?: boolean }).financeEnabled === false) {
          alert('이 교회는 재정 서비스를 사용하지 않습니다. 플랫폼 운영사에 문의해 주세요.');
          return;
        }
        saveUserSession(consoleKey, res.data.user);
        navigate(consoleInfo.path, { replace: true });
      } else {
        alert(res.data?.message || '아이디 또는 비밀번호가 올바르지 않습니다.');
      }
    } catch (err: unknown) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : null;
      alert(msg || '로그인에 실패했습니다. 다시 시도하세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Header />

      <section className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· {consoleInfo.label} 로그인</span>
          </div>
          <h1 className="page-title">{consoleInfo.label} 로그인</h1>
          <p className="page-sub">{consoleInfo.label} 기능은 로그인 후 이용하실 수 있습니다.</p>
        </div>
      </section>

      <section className="login-section">
        <div className="container">
          <div className="login-form">
            <div className="login-form__field">
              <label className="login-form__label" htmlFor="login-account">
                아이디 (이메일)
              </label>
              <input
                id="login-account"
                className="login-form__input"
                type="email"
                value={userAccount}
                placeholder="이메일을 입력하세요"
                autoComplete="username"
                disabled={loading}
                onChange={(e) => setUserAccount(e.target.value)}
              />
            </div>

            <div className="login-form__field">
              <label className="login-form__label" htmlFor="login-password">
                비밀번호
              </label>
              <input
                id="login-password"
                className="login-form__input"
                type="password"
                value={password}
                placeholder="비밀번호를 입력하세요"
                autoComplete="current-password"
                disabled={loading}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void login();
                }}
              />
            </div>

            <div className="login-form__actions">
              <button
                type="button"
                className="login-form__button"
                disabled={loading}
                onClick={() => void login()}
              >
                {loading ? '로그인 중...' : '로그인'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
}
